/**
 * usePresentationBreakpoint — canonical responsive hook for all presentation components.
 *
 * Drop-in replacement for the 9× duplicated useIsMobile() defined locally in each showcase.
 * Uses the official breakpoints token.
 *
 * Usage:
 *   const isMobile = usePresentationBreakpoint();       // default: 768px
 *   const isCompact = usePresentationBreakpoint(360);   // compact phones
 *   const isTablet = usePresentationBreakpoint(1024);   // tablet threshold
 */
import { useState, useEffect } from 'react';
import { breakpoints } from '../tokens/index';

export function usePresentationBreakpoint(bp = breakpoints.mobile) {
  const [isBelow, setIsBelow] = useState(
    () => typeof window !== 'undefined' ? window.innerWidth < bp : false
  );
  useEffect(() => {
    const fn = () => setIsBelow(window.innerWidth < bp);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, [bp]);
  return isBelow;
}

// Convenience aliases matching common usage patterns
export const useIsMobile  = () => usePresentationBreakpoint(breakpoints.mobile);
export const useIsCompact = () => usePresentationBreakpoint(breakpoints.compact);
export const useIsTablet  = () => usePresentationBreakpoint(breakpoints.tablet);

export default usePresentationBreakpoint;
